import "@shopify/polaris/build/esm/styles.css"
import "./spotlight.css"

declare global {
  interface Window {
    shopify?: {
      navigation?: {
        navigate: (path: string) => void
      }
    }
  }
}

import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
} from "@apollo/client"
import { useAppBridge } from "@shopify/app-bridge-react"
import {
  BlockStack,
  Button,
  Card,
  Layout,
  AppProvider as PolarisProvider,
  SkeletonBodyText,
  SkeletonDisplayText,
  SkeletonPage,
  Text,
} from "@shopify/polaris"
import { Outlet, RouterProvider, createBrowserRouter } from "react-router-dom"
import { Suspense, createContext, useEffect, useState } from "react"

import ConfirmPaymentPage from "./components/ConfirmPaymentPage"
import ConfirmSyncPage from "./components/ConfirmSyncPage"
import ExitIframe from "./components/ExitIframe"
import HomePage from "./components/HomePage"
import PricingPage from "./components/PricingPage"
import type { ShopFrontend } from "../typings/types"
import dayjs from "dayjs"
import enPolarisTranslations from "@shopify/polaris/locales/en.json"
import { useTranslation } from "react-i18next"

type PolarisTranslations = typeof enPolarisTranslations

const supportedLocales = {
  en: {
    dayjs: null,
    polaris: null,
  },
  de: {
    dayjs: () => import("dayjs/locale/de"),
    polaris: () => import("@shopify/polaris/locales/de.json"),
  },
  es: {
    dayjs: () => import("dayjs/locale/es"),
    polaris: () => import("@shopify/polaris/locales/es.json"),
  },
  fr: {
    dayjs: () => import("dayjs/locale/fr"),
    polaris: () => import("@shopify/polaris/locales/fr.json"),
  },
  ja: {
    dayjs: () => import("dayjs/locale/ja"),
    polaris: () => import("@shopify/polaris/locales/ja.json"),
  },
  nl: {
    dayjs: () => import("dayjs/locale/nl"),
    polaris: () => import("@shopify/polaris/locales/nl.json"),
  },
  "pt-BR": {
    dayjs: () => import("dayjs/locale/pt-br"),
    polaris: () => import("@shopify/polaris/locales/pt-BR.json"),
  },
} as const

type SupportedLocale = keyof typeof supportedLocales

function resolveLocale(rawLocale: string | undefined): SupportedLocale | null {
  if (!rawLocale) return null
  if (rawLocale in supportedLocales) return rawLocale as SupportedLocale
  const base = rawLocale.split("-")[0]
  if (base in supportedLocales) return base as SupportedLocale
  return null
}

// Long enough that a healthy load (well under 2s) never reaches it
const SLOW_LOAD_NOTICE_MS = 4000

type ShopContextType = [
  ShopFrontend | undefined,
  React.Dispatch<React.SetStateAction<ShopFrontend | undefined>>
]

export const ShopContext = createContext<ShopContextType>(
  undefined as unknown as ShopContextType
)

function BrowserProvider() {
  const [polarisTranslations, setPolarisTranslations] =
    useState<PolarisTranslations>(enPolarisTranslations)
  return (
    <PolarisProvider i18n={polarisTranslations}>
      <MyProvider onPolarisTranslationsChange={setPolarisTranslations}>
        <Outlet />
      </MyProvider>
    </PolarisProvider>
  )
}

const router = createBrowserRouter([
  {
    path: "/exitiframe",
    element: (
      <PolarisProvider i18n={enPolarisTranslations}>
        <ExitIframe />
      </PolarisProvider>
    ),
  },
  {
    path: "/",
    element: <BrowserProvider />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: "/pricing",
        element: <PricingPage />,
      },
      {
        path: "/confirm-payment",
        element: <ConfirmPaymentPage />,
      },
      {
        path: "/confirm-sync",
        element: <ConfirmSyncPage />,
      },
    ],
  },
])

function Router() {
  return <RouterProvider router={router} />
}

export default function App() {
  return (
    <Suspense fallback={null}>
      <Router />
    </Suspense>
  )
}

function MyProvider({
  children,
  onPolarisTranslationsChange,
}: {
  children: React.ReactNode
  onPolarisTranslationsChange: (translations: PolarisTranslations) => void
}) {
  const app = useAppBridge()
  const fetch = userLoggedInFetch(app)
  const [shop, setShop] = useState<ShopFrontend>()
  const [showSlowNotice, setShowSlowNotice] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const { t, i18n } = useTranslation(["common"])

  const client = new ApolloClient({
    cache: new InMemoryCache(),
    link: new HttpLink({
      credentials: "include",
      fetch,
    }),
    // link: new WebSocketLink({
    //   uri: "wss://hasura-engine.onrender.com/v1/graphql",
    //   options: {
    //     reconnect: true,
    //     connectionParams: {
    //       headers: {
    //         "X-Hasura-User-Id": shop?.domain,
    //         "X-Hasura-Role": "user",
    //       },
    //     },
    //   },
    // }),
  })

  useEffect(() => {
    const initialLoad = async () => {
      try {
        const response = await fetch("/dbInfo")
        if (!response.ok) {
          throw new Error(`/dbInfo responded ${response.status}`)
        }
        const dbInfo: ShopFrontend = await response.json()
        setShop(dbInfo)
        const resolved = resolveLocale(dbInfo.locale)
        if (resolved && resolved !== "en") {
          await i18n.changeLanguage(resolved)
          const config = supportedLocales[resolved]
          if (config.dayjs) {
            await config.dayjs()
            dayjs.locale(resolved.toLowerCase())
          }
          if (config.polaris) {
            const polarisModule = await config.polaris()
            onPolarisTranslationsChange(
              (polarisModule as { default: PolarisTranslations }).default ??
                (polarisModule as unknown as PolarisTranslations)
            )
          }
        }
      } catch (error) {
        console.error("Failed to load shop details:", error)
        setLoadFailed(true)
      }
    }
    initialLoad()
  }, [])

  useEffect(() => {
    if (shop) return
    const timer = setTimeout(() => setShowSlowNotice(true), SLOW_LOAD_NOTICE_MS)
    return () => clearTimeout(timer)
  }, [shop])

  return (
    <ApolloProvider client={client}>
      <ShopContext.Provider value={[shop, setShop]}>
        {!shop ? (
          <SkeletonPage>
            <Layout>
              {loadFailed ? (
                <Layout.Section>
                  <BlockStack gap="200" inlineAlign="start">
                    <Text as="p" tone="critical">
                      {t("loadFailed")}
                    </Text>
                    <Button onClick={() => window.location.reload()}>
                      {t("tryAgain")}
                    </Button>
                  </BlockStack>
                </Layout.Section>
              ) : (
                showSlowNotice && (
                  <Layout.Section>
                    <Text as="p" tone="subdued">
                      {t("loadingSlow")}
                    </Text>
                  </Layout.Section>
                )
              )}
              <Layout.Section>
                <Card>
                  <BlockStack gap="400">
                    <SkeletonDisplayText size="small" />
                    <SkeletonBodyText lines={3} />
                  </BlockStack>
                </Card>
              </Layout.Section>
              <Layout.Section>
                <Card>
                  <BlockStack gap="400">
                    <SkeletonDisplayText size="small" />
                    <SkeletonBodyText lines={2} />
                  </BlockStack>
                </Card>
              </Layout.Section>
            </Layout>
          </SkeletonPage>
        ) : (
          children
        )}
      </ShopContext.Provider>
    </ApolloProvider>
  )
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error("Timeout")), ms)
    ),
  ])
}

export function userLoggedInFetch(app: {
  idToken: () => Promise<string>
}): typeof globalThis.fetch {
  return async (uri: RequestInfo | URL, options: RequestInit = {}) => {
    let token: string | undefined
    try {
      token = await withTimeout(app.idToken(), 5000)
    } catch (e) {
      console.error("Failed to get idToken:", e)
    }

    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    }
    if (token) {
      headers["Authorization"] = `Bearer ${token}`
    }

    const response = await fetch(uri, {
      ...options,
      headers,
    })

    if (
      response.headers.get("X-Shopify-API-Request-Failure-Reauthorize") === "1"
    ) {
      const authUrlHeader = response.headers.get(
        "X-Shopify-API-Request-Failure-Reauthorize-Url"
      )

      if (window.shopify?.navigation) {
        window.shopify.navigation.navigate(authUrlHeader || `/auth`)
      } else {
        window.location.href = authUrlHeader || `/auth`
      }
      return new Response()
    }

    return response
  }
}
